import { confirmApp } from '../common/appDialog';
import React, { useEffect, useMemo, useState } from 'react';
import { openClientDashboard, openOrganizationDashboard } from '../common/openOrganizationDashboard';
import { getFunctions, httpsCallable } from 'firebase/functions';
import type { Organization, UserProfile, UserRole } from '@types';
import { isActiveRegionalConsultant, type OrganizationRegion } from '../common/adminAccess';
import { getOrganizationDisplayName } from '@common/organizationDisplayName';
import { COUNTRIES } from '../../services/locationData';
import { COUNTRY_CODES } from '../common/data';
import AddressSubdivisionField from './AddressSubdivisionField';
import { loadGoogleMaps } from '../../services/googleMapsLoader';
import { geocode } from '../../services/mapboxService';
import OrganizationMap from './OrganizationMap';
import PlatformUserEditorModal, { type PlatformUserUpdate } from './PlatformUserEditorModal';
import { USER_ROLES, getUserRoleLabel, getUserStatusLabel } from '../utils/userDisplayLabels';
import OrganizationCommunicationPanel from './support/OrganizationCommunicationPanel';
import OrganizationSubscriptionStatusPanel from './OrganizationSubscriptionStatusPanel';
import { subscriptionStatusLabel } from '../utils/subscriptionStatusLabel';
import PlatformServiceRequestsPanel from './PlatformServiceRequestsPanel';
import ConsultantReportsPanel from './ConsultantReportsPanel';
import { managementSubnavStyles as tabStyles } from '../common/managementSubnavStyles';
import { managementButtonStyles as buttonStyles } from '../common/managementButtonStyles';
import FreeTrialLinkModal from './FreeTrialLinkModal';
import MsaFootprintStep from './MsaFootprintStep';
import { areOrganizationLocationsLocked, arrangeMsaFootprint, msaFootprintError } from '../../functions/src/common/msaFootprint';

const statuses = ['active', 'pending', 'suspended', 'pending_approval'] as const;
const entityTypes = ['Corporation', 'Limited Liability Company', 'Partnership', 'Nonprofit', 'Government Entity', 'Other'];
const industries = ['Technology', 'Finance', 'Legal', 'Professional Services', 'Retail', 'Energy', 'Pharma', 'Mining', 'Manufacturing', 'Logistics', 'Government Contractors', 'Defence', 'Critical Infrastructure', 'Humanitarian / NGO', 'High-Risk Environments', 'Conflict Zone'];
const employeeRanges = ['Under 500', '500-2000', '2001-5000', '5001-15000', 'Over 15000'];
const revenueRanges = ['Under $5M', '$5M-$25M', '$25M-$100M', '$100M-$500M', 'Over $500M'];

interface OrganizationForm {
    isInternal: boolean;
    isTestOrganization: boolean;
    name: string;
    brandName: string;
    ownerName: string;
    ownerEmail: string;
    industry: string;
    entityType: string;
    countryOfIncorporation: string;
    employeeRange: string;
    annualRevenueRange: string;
    referralSource: string;
    referredBy: string;
    hqSameAsRegistered: boolean;
    billingSameAsRegistered: boolean;
    additionalLocations: NonNullable<Organization['additionalLocations']>;
    regionalClusters: NonNullable<Organization['regionalClusters']>;
    region: OrganizationRegion;
    regionalConsultantUid: string;
    hasSecurityProfessional: boolean;
    securityProfessionalUid: string;
    headquartersAddress: {
        addressLine1: string;
        addressLine2: string;
        city: string;
        administrativeArea: string;
        postalCode: string;
        country: string;
        countryCode: string;
    };
}

interface UserForm {
    email: string;
    displayName: string;
    role: UserRole;
    status: typeof statuses[number];
}

const emptyOrganization: OrganizationForm = {
    isInternal: false,
    isTestOrganization: false,
    name: '',
    brandName: '',
    ownerName: '',
    ownerEmail: '',
    industry: '',
    entityType: '',
    countryOfIncorporation: '',
    employeeRange: '',
    annualRevenueRange: '',
    referralSource: '',
    referredBy: '',
    hqSameAsRegistered: true,
    billingSameAsRegistered: true,
    additionalLocations: [],
    regionalClusters: [],
    region: 'Global',
    regionalConsultantUid: '',
    hasSecurityProfessional: false,
    securityProfessionalUid: '',
    headquartersAddress: {
        addressLine1: '',
        addressLine2: '',
        city: '',
        administrativeArea: '',
        postalCode: '',
        country: '',
        countryCode: '',
    },
};

const emptyUser: UserForm = {
    email: '',
    displayName: '',
    role: 'employee',
    status: 'pending',
};

async function geocodeOrganization(form: OrganizationForm) {
    const address = form.headquartersAddress;
    if (!Object.values(address).some(value => value.trim()))
        return null;
    if (!address.addressLine1.trim() || !address.city.trim() || !address.country.trim())
        return null;
    await loadGoogleMaps();
    const query = [address.addressLine1, address.addressLine2, address.city, address.administrativeArea, address.postalCode, address.country]
        .map(value => value.trim())
        .filter(Boolean)
        .join(', ');
    const result = await geocode(query);
    return {
        latitude: result.lat,
        longitude: result.lng,
        formattedAddress: result.formattedAddress || query,
        isExact: result.isExact,
    };
}

async function geocodeAdditionalLocations(locations: OrganizationForm['additionalLocations']) {
    await loadGoogleMaps();
    const verified: OrganizationForm['additionalLocations'] = [];
    for (let index = 0; index < locations.length; index++) {
        const location = locations[index];
        if (!location.addressLine1.trim() || !location.city.trim() || !location.postalCode.trim() || !location.country.trim())
            throw new Error(`Complete the full address for additional location ${index + 1}.`);
        const query = [location.addressLine1, location.addressLine2, location.city, location.administrativeArea, location.postalCode, location.country].map(value => String(value || '').trim()).filter(Boolean).join(', ');
        const result = await geocode(query);
        verified.push({ ...location, coordinates: { latitude: result.lat, longitude: result.lng, formattedAddress: result.formattedAddress || query, isExact: result.isExact } });
    }
    return verified;
}

const Field: React.FC<React.InputHTMLAttributes<HTMLInputElement> & { label: string }> = ({ label, ...props }) => (
    <label className="block">
        <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">{label}</span>
        <input {...props} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
    </label>
);

const SelectField: React.FC<React.SelectHTMLAttributes<HTMLSelectElement> & { label: string }> = ({ label, children, ...props }) => (
    <label className="block">
        <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">{label}</span>
        <select {...props} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20">
            {children}
        </select>
    </label>
);

const subdivisionInputClass = 'mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20';

const Modal: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({ title, onClose, children }) => (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
        <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-5 flex items-center justify-between">
                <h2 className="text-xl font-extrabold text-slate-900">{title}</h2>
                <button onClick={onClose} className="rounded-lg px-3 py-1 text-xl text-slate-400 hover:bg-slate-100 hover:text-slate-700">X</button>
            </div>
            {children}
        </div>
    </div>
);

class OrganizationMapBoundary extends React.Component<{ children: React.ReactNode; onOpenList: () => void }, { failed: boolean }> {
    state = { failed: false };

    static getDerivedStateFromError() {
        return { failed: true };
    }

    componentDidCatch(error: Error) {
        console.error('Organization map failed:', error);
    }

    render() {
        if (this.state.failed)
            return <div role="alert" className="rounded-2xl border border-red-200 bg-red-50 p-6 text-sm font-semibold text-red-700">Organization map is unavailable. <button type="button" onClick={this.props.onOpenList} className="font-bold underline">Open organization list</button></div>;
        return this.props.children;
    }
}

interface PlatformOrganizationManagementProps {
    currentUser: UserProfile;
    openOrganizationId?: string | null;
    onOpenOrganizationHandled?: () => void;
}

const PlatformOrganizationManagement: React.FC<PlatformOrganizationManagementProps> = ({ currentUser, openOrganizationId, onOpenOrganizationHandled }) => {
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [users, setUsers] = useState<UserProfile[]>([]);
    const [regionalAdmins, setRegionalAdmins] = useState<UserProfile[]>([]);
    const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | null>(null);
    const [organizationForm, setOrganizationForm] = useState<OrganizationForm>(emptyOrganization);
    const [userForm, setUserForm] = useState<UserForm>(emptyUser);
    const [editingUser, setEditingUser] = useState<UserProfile | null>(null);
    const [showOrganizationModal, setShowOrganizationModal] = useState(false);
    const [showTrialLinkModal, setShowTrialLinkModal] = useState(false);
    const [showUserModal, setShowUserModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [openingOrganization, setOpeningOrganization] = useState<string | null>(null);
    const openDashboard = async (organizationId: string, clientView = false) => {
        setMessage(null);
        setOpeningOrganization(organizationId);
        try {
            await (clientView ? openClientDashboard(organizationId) : openOrganizationDashboard(organizationId));
        } catch (error) {
            setMessage(error instanceof Error ? error.message : 'Could not open the client dashboard.');
        } finally {
            setOpeningOrganization(null);
        }
    };
    const [search, setSearch] = useState('');
    const [activeTab, setActiveTab] = useState<'map' | 'organizations' | 'awaiting'>('map');
    const [organizationDetailTab, setOrganizationDetailTab] = useState<'details' | 'users' | 'subscription' | 'services' | 'reports' | 'invoices' | 'communication'>('details');

    const callManagement = async (action: string, data: Record<string, unknown>) => {
        const callable = httpsCallable(getFunctions(undefined, 'us-central1'), 'inviteTeamMember');
        return callable({ action, data });
    };

    const load = async () => {
        setLoading(true);
        try {
            const response = await callManagement('listDirectory', {});
            const result = response.data as { organizations: Organization[]; users: UserProfile[]; admins: UserProfile[]; consultants?: UserProfile[] };
            setOrganizations(result.organizations
                .filter(organization => !organization.isDeleted)
                .sort((left, right) => getOrganizationDisplayName(left).localeCompare(getOrganizationDisplayName(right), undefined, { sensitivity: 'base' })));
            setUsers(result.users
                .filter(user => !user.isDeleted && user.status !== 'deleted')
                .sort((left, right) => (left.displayName || left.email).localeCompare(right.displayName || right.email, undefined, { sensitivity: 'base' })));
            setRegionalAdmins((result.consultants || result.admins || []).filter(isActiveRegionalConsultant).sort((left, right) => (left.displayName || left.email).localeCompare(right.displayName || right.email)));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load().catch(error => setMessage(error.message || 'Failed loading organizations.'));
    }, [currentUser.adminRoles, currentUser.regions]);

    const selectedOrganization = organizations.find(organization => organization.id === selectedOrganizationId) || null;
    const organizationUsers = useMemo(
        () => users.filter(user => user.organizationId === selectedOrganizationId),
        [users, selectedOrganizationId],
    );

    const ownerName = (organization: Organization) => {
        const owner = users.find(user => user.uid === organization.ownerId) ||
            users.find(user => user.email === organization.ownerEmail) ||
            users.find(user => user.organizationId === organization.id && user.role === 'owner');
        return owner?.displayName || owner?.email || organization.ownerEmail || 'Unassigned';
    };

    const selectOrganization = (organization: Organization) => {
        const footprint = arrangeMsaFootprint(organization.headquartersAddress?.country || '', organization.additionalLocations || [], organization.regionalClusters || []);
        setOrganizationDetailTab('details');
        setSelectedOrganizationId(organization.id);
        setOrganizationForm({
            isInternal: organization.isInternal === true,
            isTestOrganization: organization.isTestOrganization === true,
            name: organization.name || '',
            brandName: organization.brandName || organization.name || '',
            ownerName: ownerName(organization),
            ownerEmail: organization.ownerEmail || '',
            industry: organization.industry || '',
            entityType: organization.entityType || '',
            countryOfIncorporation: organization.countryOfIncorporation || '',
            employeeRange: organization.employeeRange || '',
            annualRevenueRange: organization.annualRevenueRange || '',
            referralSource: organization.referralSource || '',
            referredBy: organization.referredBy || '',
            hqSameAsRegistered: organization.hqSameAsRegistered === true,
            billingSameAsRegistered: organization.billingSameAsRegistered === true,
            additionalLocations: footprint.locations as OrganizationForm['additionalLocations'],
            regionalClusters: footprint.clusters,
            region: organization.region || 'Global',
            regionalConsultantUid: organization.regionalConsultantUid || '',
            hasSecurityProfessional: organization.hasSecurityProfessional === true,
            securityProfessionalUid: organization.securityProfessionalUid || '',
            headquartersAddress: {
                addressLine1: organization.headquartersAddress?.addressLine1 || '',
                addressLine2: organization.headquartersAddress?.addressLine2 || '',
                city: organization.headquartersAddress?.city || '',
                administrativeArea: organization.headquartersAddress?.administrativeArea || '',
                postalCode: organization.headquartersAddress?.postalCode || '',
                country: organization.headquartersAddress?.country || '',
                countryCode: organization.headquartersAddress?.countryCode || '',
            },
        });
        setMessage(null);
    };

    useEffect(() => {
        if (!openOrganizationId || loading)
            return;
        const organization = organizations.find(candidate => candidate.id === openOrganizationId);
        if (organization)
            selectOrganization(organization);
        onOpenOrganizationHandled?.();
    }, [openOrganizationId, loading, organizations]);

    const awaitingOrganizations = organizations.filter(organization =>
        organization.isInternal !== true &&
        (organization.isTestOrganization === true || organization.hasMsaSubscription === true &&
            ['active', 'trialing', 'on_trial'].includes(organization.subscriptionStatus || '')) &&
        !organization.regionalConsultantUid
    );
    const mapOrganizations = useMemo(() => organizations.filter(organization => organization.isInternal !== true), [organizations]);
    const visibleOrganizations = activeTab === 'awaiting' ? awaitingOrganizations : organizations;
    const filteredOrganizations = visibleOrganizations.filter(organization => {
        const term = search.trim().toLowerCase();
        if (!term)
            return true;
        return [getOrganizationDisplayName(organization), organization.name, ownerName(organization), organization.ownerEmail]
            .some(value => String(value || '').toLowerCase().includes(term));
    });

    const run = async (action: string, data: Record<string, unknown>, success: string) => {
        setSaving(true);
        setMessage(null);
        try {
            const response = await callManagement(action, data);
            const result = response.data as {
                invitationSent?: boolean;
                invitationError?: string;
                invitationId?: string;
                region?: OrganizationRegion;
            };
            if (action === 'resendInvitation' && !result.invitationId)
                throw new Error('Postmark returned no delivery receipt.');
            await load();
            setMessage(result.invitationSent === false ? `${success} Invitation failed: ${result.invitationError || 'Unknown delivery error.'}` : success);
            return result;
        } catch (error: any) {
            setMessage(error.message || 'Management action failed.');
            return false;
        } finally {
            setSaving(false);
        }
    };

    const createOrganization = async (event: React.FormEvent) => {
        event.preventDefault();
        setSaving(true);
        try {
            const headquartersCoordinates = await geocodeOrganization(organizationForm);
            if (await run('createOrganization', { ...organizationForm, headquartersCoordinates }, 'Organization created.')) {
                setShowOrganizationModal(false);
                setOrganizationForm(emptyOrganization);
            }
        } catch (error: any) {
            setMessage(error.message || 'Headquarters geocoding failed.');
            setSaving(false);
        }
    };

    const updateOrganization = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!selectedOrganizationId || !selectedOrganization)
            return;
        setSaving(true);
        try {
            const locationsLocked = areOrganizationLocationsLocked(selectedOrganization);
            const footprintError = !locationsLocked && (organizationForm.additionalLocations.length || organizationForm.regionalClusters.length)
                ? msaFootprintError(organizationForm.headquartersAddress.country, organizationForm.additionalLocations, organizationForm.regionalClusters) : '';
            if (footprintError)
                throw new Error(footprintError);
            const headquartersCoordinates = await geocodeOrganization(organizationForm);
            const additionalLocations = locationsLocked ? undefined : await geocodeAdditionalLocations(organizationForm.additionalLocations);
            const result = await run('updateOrganization', {
                name: organizationForm.name,
                isInternal: organizationForm.isInternal,
                ...(currentUser.adminRole === 'Super' ? { isTestOrganization: organizationForm.isTestOrganization } : {}),
                brandName: organizationForm.brandName,
                industry: organizationForm.industry,
                entityType: organizationForm.entityType,
                countryOfIncorporation: organizationForm.countryOfIncorporation,
                employeeRange: organizationForm.employeeRange,
                annualRevenueRange: organizationForm.annualRevenueRange,
                referralSource: organizationForm.referralSource,
                referredBy: organizationForm.referredBy,
                hqSameAsRegistered: organizationForm.hqSameAsRegistered,
                billingSameAsRegistered: organizationForm.billingSameAsRegistered,
                ...(!locationsLocked ? { additionalLocations, regionalClusters: organizationForm.regionalClusters } : {}),
                headquartersAddress: organizationForm.headquartersAddress,
                headquartersCoordinates,
                organizationId: selectedOrganizationId,
                regionalConsultantUid: organizationForm.regionalConsultantUid || null,
                hasSecurityProfessional: organizationForm.hasSecurityProfessional,
                securityProfessionalUid: organizationForm.hasSecurityProfessional ? organizationForm.securityProfessionalUid : null,
            }, 'Organization updated.');
            if (result !== false && result.region)
                setOrganizationForm(previous => ({ ...previous, region: result.region! }));
        } catch (error: any) {
            setMessage(error.message || 'Headquarters geocoding failed.');
            setSaving(false);
        }
    };

    const deleteOrganization = async (organization: Organization) => {
        if (!await confirmApp(`Delete ${getOrganizationDisplayName(organization)}? All member access will be disabled.`))
            return;
        if (await run('deleteOrganization', { organizationId: organization.id }, 'Organization deleted.'))
            setSelectedOrganizationId(null);
    };

    const openAddUser = () => {
        setEditingUser(null);
        setUserForm(emptyUser);
        setShowUserModal(true);
    };

    const openEditUser = (user: UserProfile) => {
        setEditingUser(user);
        setShowUserModal(true);
    };

    const saveUser = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!selectedOrganizationId)
            return;
        if (await run('createUser', { ...userForm, organizationId: selectedOrganizationId }, 'User created.'))
            setShowUserModal(false);
    };

    const saveEditedUser = async (update: PlatformUserUpdate) => {
        if (!selectedOrganizationId || !editingUser)
            return;
        if (await run('updateUser', { ...update, email: editingUser.email, organizationId: selectedOrganizationId }, 'User updated.'))
            setShowUserModal(false);
    };

    const deleteUser = async (user: UserProfile) => {
        if (!selectedOrganizationId || !await confirmApp(`Delete ${user.displayName || user.email}? Their access will be disabled.`))
            return;
        await run('deleteUser', { organizationId: selectedOrganizationId, email: user.email }, 'User deleted.');
    };

    const transferOwnership = async (user: UserProfile) => {
        if (!selectedOrganization || saving)
            return;
        const previousOwnerEmail = selectedOrganization.ownerEmail || organizationUsers.find(member => member.role === 'owner')?.email;
        if (!previousOwnerEmail) {
            setMessage('Current owner not found. Reload the organization before transferring ownership.');
            return;
        }
        if (!await confirmApp(
            `Transfer ownership of ${getOrganizationDisplayName(selectedOrganization)} from ${ownerName(selectedOrganization)} (${previousOwnerEmail}) to ${user.displayName || user.email} (${user.email})?\n\nThe new owner will have full owner access. The current owner will remain an organization administrator.`,
            { title: 'Transfer Ownership', confirmLabel: 'Transfer Ownership' },
        ))
            return;
        await run('transferOwnership', { organizationId: selectedOrganization.id, email: user.email, previousOwnerEmail }, 'Organization ownership transferred.');
    };

    const resendInvitation = async (user: UserProfile) => {
        if (!selectedOrganizationId)
            return;
        await run('resendInvitation', { organizationId: selectedOrganizationId, email: user.email }, 'Invitation sent.');
    };

    const toggleSuspension = async (user: UserProfile) => {
        const status = user.status === 'suspended' ? 'active' : 'suspended';
        const action = status === 'suspended' ? 'suspend' : 'reactivate';
        if (!await confirmApp(`${action === 'suspend' ? 'Suspend' : 'Reactivate'} ${user.displayName || user.email}?`))
            return;
        setSaving(true);
        setMessage(null);
        try {
            if (!selectedOrganizationId)
                return;
            await callManagement('updateUser', {
                organizationId: selectedOrganizationId,
                email: user.email,
                displayName: user.displayName,
                role: user.role,
                status,
            });
            await load();
            setMessage(`User ${action}d.`);
        } catch (error: any) {
            setMessage(error.message || `Failed to ${action} user.`);
        } finally {
            setSaving(false);
        }
    };

    if (loading)
        return <div className="py-20 text-center text-sm font-semibold text-slate-400">Loading organizations...</div>;

    if (selectedOrganization) {
        return (
            <div className="animate-fade-in space-y-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <button onClick={() => setSelectedOrganizationId(null)} className="text-sm font-bold text-blue-600 hover:text-blue-800">Back to Organizations</button>
                    <button type="button" disabled={openingOrganization !== null} onClick={() => openDashboard(selectedOrganization.id)} className="rounded bg-blue-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                        {openingOrganization ? 'Opening…' : 'Open management dashboard ↗'}
                    </button>
                    <button type="button" disabled={openingOrganization !== null} onClick={() => openDashboard(selectedOrganization.id, true)} className={`rounded px-4 py-2 text-sm font-semibold ${buttonStyles.primary}`}>
                        Open client dashboard ↗
                    </button>
                    <button
                        onClick={() => deleteOrganization(selectedOrganization)}
                        disabled={saving}
                        className="rounded-lg border border-red-200 px-4 py-2 text-sm font-bold text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        Delete Organization
                    </button>
                </div>

                {message && <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-700">{message}</div>}

                <nav aria-label="Organization" className={tabStyles.tabs}>
                    {([
                        ['details', 'Details'],
                        ['users', 'Users'],
                        ['subscription', 'Subscription Status'],
                        ['services', 'Service Requests'],
                        ['reports', 'On Demand Reports'],
                        ['invoices', 'Invoices'],
                        ['communication', 'Communication'],
                    ] as const).map(([key, label]) => (
                        <button key={key} type="button" aria-current={organizationDetailTab === key ? 'page' : undefined} onClick={() => setOrganizationDetailTab(key)} className={`${tabStyles.tab} ${organizationDetailTab === key ? tabStyles.activeTab : tabStyles.inactiveTab}`}>{label}</button>
                    ))}
                </nav>

                {organizationDetailTab === 'details' && <form onSubmit={updateOrganization} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <div className="mb-5">
                        <h2 className="text-2xl font-extrabold text-slate-900">{getOrganizationDisplayName(selectedOrganization)}</h2>
                        <p className="text-xs font-mono text-slate-400">{selectedOrganization.id}</p>
                    </div>
                    <div className="grid gap-4 md:grid-cols-2">
                        <Field label="Brand Name" value={organizationForm.brandName} onChange={event => setOrganizationForm({ ...organizationForm, brandName: event.target.value })} required />
                        <Field label="Legal/Internal Name" value={organizationForm.name} onChange={event => setOrganizationForm({ ...organizationForm, name: event.target.value })} required />
                        <Field label="Owner" value={organizationForm.ownerName} disabled />
                        <Field label="Owner Email" value={organizationForm.ownerEmail} disabled />
                        <Field label="MSA Reference" value={selectedOrganization.msaReference || 'Not recorded'} disabled />
                        <label className="md:col-span-2 flex items-center gap-3 rounded-lg border border-slate-200 p-3 text-sm font-semibold text-slate-700"><input type="checkbox" checked={organizationForm.isInternal} onChange={event => setOrganizationForm({ ...organizationForm, isInternal: event.target.checked })} className="h-4 w-4 accent-blue-600" />Internal organization<span className="font-normal text-slate-500">Hide from map and consultant assignment queue.</span></label>
                        {currentUser.adminRole === 'Super' && <label className="md:col-span-2 flex items-center gap-3 rounded-lg border border-slate-200 p-3 text-sm font-semibold text-slate-700"><input type="checkbox" checked={organizationForm.isTestOrganization} onChange={event => setOrganizationForm({ ...organizationForm, isTestOrganization: event.target.checked })} className="h-4 w-4 accent-blue-600" />Test Organization<span className="font-normal text-slate-500">No subscription required. Service requests stay unlimited and use normal billing choices.</span></label>}
                        <SelectField label="Industry" value={organizationForm.industry} onChange={event => setOrganizationForm({ ...organizationForm, industry: event.target.value })}>
                            <option value="">Select industry</option>
                            {organizationForm.industry && !industries.includes(organizationForm.industry) && <option>{organizationForm.industry}</option>}
                            {industries.map(value => <option key={value}>{value}</option>)}
                        </SelectField>
                        <SelectField label="Legal Entity Type" value={organizationForm.entityType} onChange={event => setOrganizationForm({ ...organizationForm, entityType: event.target.value })}>
                            <option value="">Select type</option>
                            {entityTypes.map(value => <option key={value}>{value}</option>)}
                        </SelectField>
                        <SelectField label="Country Of Incorporation" value={organizationForm.countryOfIncorporation} onChange={event => setOrganizationForm({ ...organizationForm, countryOfIncorporation: event.target.value })}>
                            <option value="">Select country</option>
                            {COUNTRIES.map(country => <option key={country} value={country}>{country}</option>)}
                        </SelectField>
                        <SelectField label="Employee Headcount" value={organizationForm.employeeRange} onChange={event => setOrganizationForm({ ...organizationForm, employeeRange: event.target.value })}>
                            <option value="">Select range</option>
                            {employeeRanges.map(value => <option key={value}>{value}</option>)}
                        </SelectField>
                        <SelectField label="Annual Revenue" value={organizationForm.annualRevenueRange} onChange={event => setOrganizationForm({ ...organizationForm, annualRevenueRange: event.target.value })}>
                            <option value="">Select range</option>
                            {revenueRanges.map(value => <option key={value}>{value}</option>)}
                        </SelectField>
                        <Field label="Referral Source" value={organizationForm.referralSource} onChange={event => setOrganizationForm({ ...organizationForm, referralSource: event.target.value })} />
                        <Field label="Referred By" value={organizationForm.referredBy} onChange={event => setOrganizationForm({ ...organizationForm, referredBy: event.target.value })} />
                        <Field label="Region" value={organizationForm.region} disabled />
                        <SelectField label="Assigned Consultant" value={organizationForm.regionalConsultantUid} disabled={!['Super', 'Global'].includes(String(currentUser.adminRole))} onChange={event => setOrganizationForm({ ...organizationForm, regionalConsultantUid: event.target.value })}>
                            <option value="">Unassigned</option>
                            {regionalAdmins.map(admin => <option key={admin.uid} value={admin.uid}>{admin.displayName || admin.email}{admin.isTestConsultant ? ' (Test Consultant)' : ''}</option>)}
                        </SelectField>
                        <label className="flex items-center gap-3 rounded-lg border border-slate-200 p-3 text-sm font-semibold text-slate-700"><input type="checkbox" checked={organizationForm.hasSecurityProfessional} onChange={event => setOrganizationForm({ ...organizationForm, hasSecurityProfessional: event.target.checked })} />Client has security professional</label>
                        {organizationForm.hasSecurityProfessional && <SelectField label="Security Professional" value={organizationForm.securityProfessionalUid} onChange={event => setOrganizationForm({ ...organizationForm, securityProfessionalUid: event.target.value })}><option value="">Select user</option>{organizationUsers.filter(user => user.status === 'active' && user.uid).map(user => <option key={user.uid} value={user.uid}>{user.displayName || user.email}</option>)}</SelectField>}
                        <div className="md:col-span-2 mt-2 border-t border-slate-200 pt-4"><h3 className="font-extrabold text-slate-900">Headquarters Address</h3><p className="text-xs text-slate-500">Region updates automatically after saving a complete address.</p></div>
                        <div className="md:col-span-2"><SelectField label="Country" value={organizationForm.headquartersAddress.country} onChange={event => setOrganizationForm({ ...organizationForm, headquartersAddress: { ...organizationForm.headquartersAddress, country: event.target.value, countryCode: (COUNTRY_CODES as any)[event.target.value]?.iso2 || '', administrativeArea: '' } })}><option value="">Select country</option>{COUNTRIES.map(country => <option key={country} value={country}>{country}</option>)}</SelectField></div>
                        <Field label="Address Line 1" value={organizationForm.headquartersAddress.addressLine1} onChange={event => setOrganizationForm({ ...organizationForm, headquartersAddress: { ...organizationForm.headquartersAddress, addressLine1: event.target.value } })} />
                        <Field label="Address Line 2" value={organizationForm.headquartersAddress.addressLine2} onChange={event => setOrganizationForm({ ...organizationForm, headquartersAddress: { ...organizationForm.headquartersAddress, addressLine2: event.target.value } })} />
                        <Field label="City / Locality" value={organizationForm.headquartersAddress.city} onChange={event => setOrganizationForm({ ...organizationForm, headquartersAddress: { ...organizationForm.headquartersAddress, city: event.target.value } })} />
                        <AddressSubdivisionField country={organizationForm.headquartersAddress.country} value={organizationForm.headquartersAddress.administrativeArea} onChange={value => setOrganizationForm({ ...organizationForm, headquartersAddress: { ...organizationForm.headquartersAddress, administrativeArea: value } })} className={subdivisionInputClass} />
                        <Field label="Postal Code" value={organizationForm.headquartersAddress.postalCode} onChange={event => setOrganizationForm({ ...organizationForm, headquartersAddress: { ...organizationForm.headquartersAddress, postalCode: event.target.value } })} />
                        <label className="flex items-center gap-3 rounded-lg border border-slate-200 p-3 text-sm font-semibold text-slate-700"><input type="checkbox" checked={organizationForm.hqSameAsRegistered} onChange={event => setOrganizationForm({ ...organizationForm, hqSameAsRegistered: event.target.checked })} />Headquarters matches registered address</label>
                        <label className="flex items-center gap-3 rounded-lg border border-slate-200 p-3 text-sm font-semibold text-slate-700"><input type="checkbox" checked={organizationForm.billingSameAsRegistered} onChange={event => setOrganizationForm({ ...organizationForm, billingSameAsRegistered: event.target.checked })} />Billing matches registered address</label>
                        <div className="md:col-span-2 mt-2 border-t border-slate-200 pt-4"><h3 className="font-extrabold text-slate-900">Additional Locations</h3><p className="text-xs text-slate-500">{selectedOrganization.isTestOrganization === true ? 'Test organizations can add locations at any time.' : 'Headquarters and four additional locations are included.'}</p></div>
                        <div className="md:col-span-2">
                            {areOrganizationLocationsLocked(selectedOrganization) && <p className="mb-4 text-sm text-slate-500">Location changes are available before MSA signing.</p>}
                            <MsaFootprintStep form={organizationForm} setForm={setOrganizationForm} hqCountry={organizationForm.headquartersAddress.country}
                                locked={saving || areOrganizationLocationsLocked(selectedOrganization)} countryOptions={COUNTRIES.map(name => ({ name, code: (COUNTRY_CODES as any)[name]?.iso2 || name }))} />
                        </div>
                    </div>
                    <button disabled={saving} className="mt-5 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-50">Save Organization</button>
                </form>}

                {organizationDetailTab === 'users' && <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="flex items-center justify-between border-b border-slate-200 p-5">
                        <div>
                            <h3 className="font-extrabold text-slate-900">Organization Users</h3>
                            <p className="text-xs text-slate-400">{organizationUsers.length} active records</p>
                        </div>
                        <button onClick={openAddUser} className={`rounded-lg px-4 py-2 text-sm font-bold ${buttonStyles.primary}`}>Add User</button>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="px-5 py-3">User</th><th className="px-5 py-3">Role</th><th className="px-5 py-3">Status</th><th className="px-5 py-3 text-right">Actions</th></tr></thead>
                            <tbody className="divide-y divide-slate-100">
                                {organizationUsers.map(user => (
                                    <tr key={user.email}>
                                        <td className="px-5 py-3"><p className="font-bold text-slate-800">{user.displayName}</p><p className="text-xs text-slate-400">{user.email}</p></td>
                                        <td className="px-5 py-3">{getUserRoleLabel(user.role)}</td>
                                        <td className="px-5 py-3">{getUserStatusLabel(user.status)}</td>
                                        <td className="px-5 py-3">
                                            <div className="flex flex-wrap justify-end gap-2">
                                                {user.role !== 'owner' && user.status === 'active' && !user.adminRole && user.uid && user.email !== selectedOrganization.ownerEmail && user.uid !== selectedOrganization.ownerId &&
                                                    <button type="button" onClick={() => transferOwnership(user)} disabled={saving} className={`rounded-lg px-2 py-1 text-xs font-bold ${buttonStyles.primary}`}>Transfer Ownership</button>}
                                                <button onClick={() => toggleSuspension(user)} disabled={saving || user.email === currentUser.email} className="text-xs font-bold text-amber-600 hover:text-amber-800 disabled:opacity-30">{user.status === 'suspended' ? 'Reactivate' : 'Suspend'}</button>
                                                <button onClick={() => resendInvitation(user)} disabled={saving} className="text-xs font-bold text-emerald-600 hover:text-emerald-800 disabled:opacity-30">Send Invite</button>
                                                <button onClick={() => openEditUser(user)} className="text-xs font-bold text-blue-600 hover:text-blue-800">Edit</button>
                                                <button onClick={() => deleteUser(user)} disabled={user.email === currentUser.email || user.role === 'owner'} className="text-xs font-bold text-red-600 hover:text-red-800 disabled:opacity-30">Delete</button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {!organizationUsers.length && <p className="p-8 text-center text-sm text-slate-400">No active users.</p>}
                    </div>
                </div>}

                {organizationDetailTab === 'subscription' && <OrganizationSubscriptionStatusPanel organization={selectedOrganization} canCreateTrialLink={['Super', 'Global'].includes(currentUser.adminRole || '')} canResetMsaTrial={currentUser.adminRole === 'Super'} onReset={load} />}

                {organizationDetailTab === 'services' && (
                    <PlatformServiceRequestsPanel organizationId={selectedOrganization.id} currentUser={currentUser} showTabs={false} />
                )}

                {organizationDetailTab === 'reports' && <ConsultantReportsPanel key={selectedOrganization.id} organizationId={selectedOrganization.id} />}

                {organizationDetailTab === 'invoices' && <PlatformServiceRequestsPanel organizationId={selectedOrganization.id} currentUser={currentUser} initialTab="invoices" showTabs={false} />}

                {organizationDetailTab === 'communication' && <OrganizationCommunicationPanel key={selectedOrganization.id} organizationId={selectedOrganization.id} organizationRegion={selectedOrganization.region || 'Global'} currentUser={currentUser} />}

                {showUserModal && editingUser && <PlatformUserEditorModal user={editingUser} busy={saving} onClose={() => setShowUserModal(false)} onSave={saveEditedUser} />}
                {showUserModal && !editingUser && (
                    <Modal title="Add User" onClose={() => setShowUserModal(false)}>
                        <form onSubmit={saveUser} className="space-y-4">
                            <Field label="Name" value={userForm.displayName} onChange={event => setUserForm({ ...userForm, displayName: event.target.value })} required />
                            <Field label="Email" type="email" value={userForm.email} onChange={event => setUserForm({ ...userForm, email: event.target.value })} required />
                            <div className="grid gap-4 md:grid-cols-2">
                                <SelectField label="Role" value={userForm.role} onChange={event => setUserForm({ ...userForm, role: event.target.value as UserRole })}>{USER_ROLES.filter(role => role !== 'owner').map(role => <option key={role} value={role}>{getUserRoleLabel(role)}</option>)}</SelectField>
                            </div>
                            <button disabled={saving} className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-50">Add User</button>
                        </form>
                    </Modal>
                )}
            </div>
        );
    }

    return (
        <div className="animate-fade-in space-y-4">
            {['Super', 'Global'].includes(currentUser.adminRole || '') && <div><button type="button" onClick={() => setShowTrialLinkModal(true)} className="text-sm font-bold text-blue-600 underline underline-offset-4 hover:text-blue-700 dark:text-blue-400">Create a free trial link</button></div>}
            {showTrialLinkModal && <FreeTrialLinkModal onClose={() => setShowTrialLinkModal(false)} />}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <nav aria-label="Organizations" className={tabStyles.tabs}>
                    <button type="button" aria-current={activeTab === 'map' ? 'page' : undefined} onClick={() => setActiveTab('map')} className={`${tabStyles.tab} ${activeTab === 'map' ? tabStyles.activeTab : tabStyles.inactiveTab}`}>Organization Map</button>
                    <button type="button" aria-current={activeTab === 'organizations' ? 'page' : undefined} onClick={() => setActiveTab('organizations')} className={`${tabStyles.tab} ${activeTab === 'organizations' ? tabStyles.activeTab : tabStyles.inactiveTab}`}>All Organizations</button>
                    {['Super', 'Global'].includes(currentUser.adminRole || '') && <button type="button" aria-current={activeTab === 'awaiting' ? 'page' : undefined} onClick={() => setActiveTab('awaiting')} className={`${tabStyles.tab} ${activeTab === 'awaiting' ? tabStyles.activeTab : tabStyles.inactiveTab}`}>Awaiting Consultant Assignment</button>}
                </nav>
                <button onClick={() => { setOrganizationForm(emptyOrganization); setShowOrganizationModal(true); }} className={`rounded-lg px-4 py-2 text-sm font-bold ${buttonStyles.primary}`}>Add Organization</button>
            </div>
            {message && <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-700">{message}</div>}
            {activeTab === 'map' ? (
                <div className="space-y-4">
                    {['Super', 'Global'].includes(currentUser.adminRole || '') && awaitingOrganizations.length > 0 && <button type="button" onClick={() => setActiveTab('awaiting')} className="flex w-full items-center justify-between rounded-xl border border-amber-300 bg-amber-50 px-5 py-4 text-left text-amber-900 shadow-sm transition hover:bg-amber-100"><span className="font-extrabold">{awaitingOrganizations.length} {awaitingOrganizations.length === 1 ? 'organization is' : 'organizations are'} awaiting consultant assignment.</span><span className="text-sm font-bold">Review now →</span></button>}
                    <OrganizationMapBoundary onOpenList={() => setActiveTab('organizations')}>
                        <OrganizationMap
                            organizations={mapOrganizations}
                            regionalAdminRegions={currentUser.adminRole === 'Regional' ? currentUser.regions || [] : []}
                            onEdit={selectOrganization}
                        />
                    </OrganizationMapBoundary>
                </div>
            ) : (
                <>
                    <div className="flex flex-wrap items-center gap-3">
                        <input type="text" placeholder="Search organizations..." value={search} onChange={event => setSearch(event.target.value)} className="w-72 rounded-lg border border-slate-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40" />
                        <p className="text-sm font-medium text-slate-500">{filteredOrganizations.length} of {visibleOrganizations.length} organizations</p>
                    </div>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {filteredOrganizations.map(organization => (
                            <div key={organization.id} className="rounded-xl border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:border-blue-300 hover:shadow-md">
                                <div className="flex items-start justify-between gap-3"><h3 className="font-bold text-slate-800">{getOrganizationDisplayName(organization)}</h3><div className="flex gap-1">{organization.isInternal && <span className="rounded bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-700">Internal</span>}{organization.isTestOrganization && <span className="rounded bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-700">Test Organization</span>}</div></div>
                                <div className="mt-4 space-y-2 text-xs text-slate-500"><div className="flex justify-between gap-3"><span>Owner</span><span className="truncate font-semibold text-slate-700">{ownerName(organization)}</span></div><div className="flex justify-between gap-3"><span>Users</span><span className="font-semibold text-slate-700">{users.filter(user => user.organizationId === organization.id).length}</span></div><div className="flex justify-between gap-3"><span>Status</span><span className="font-semibold text-slate-700">{subscriptionStatusLabel(organization.subscriptionStatus, organization.isTestOrganization, 'Inactive')}</span></div></div>
                                <div className="mt-4 flex flex-wrap gap-3">
                                    <button type="button" onClick={() => selectOrganization(organization)} className="rounded border border-slate-300 px-3 py-2 text-sm font-semibold">Manage</button>
                                </div>
                            </div>
                        ))}
                    </div>
                    {!filteredOrganizations.length && <p className="rounded-xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-400">No organizations found.</p>}
                </>
            )}

            {showOrganizationModal && (
                <Modal title="Add Organization" onClose={() => setShowOrganizationModal(false)}>
                    <form onSubmit={createOrganization} className="grid gap-4 md:grid-cols-2">
                        <Field label="Brand Name" value={organizationForm.brandName} onChange={event => setOrganizationForm({ ...organizationForm, brandName: event.target.value })} required />
                        <Field label="Legal/Internal Name" value={organizationForm.name} onChange={event => setOrganizationForm({ ...organizationForm, name: event.target.value })} required />
                        <Field label="Owner Name" value={organizationForm.ownerName} onChange={event => setOrganizationForm({ ...organizationForm, ownerName: event.target.value })} required />
                        <Field label="Owner Email" type="email" value={organizationForm.ownerEmail} onChange={event => setOrganizationForm({ ...organizationForm, ownerEmail: event.target.value })} required />
                        <label className="md:col-span-2 flex items-center gap-3 rounded-lg border border-slate-200 p-3 text-sm font-semibold text-slate-700"><input type="checkbox" checked={organizationForm.isInternal} onChange={event => setOrganizationForm({ ...organizationForm, isInternal: event.target.checked })} className="h-4 w-4 accent-blue-600" />Internal organization<span className="font-normal text-slate-500">Hide from map and consultant assignment queue.</span></label>
                        <Field label="Industry" value={organizationForm.industry} onChange={event => setOrganizationForm({ ...organizationForm, industry: event.target.value })} />
                        <Field label="Region" value={organizationForm.region} disabled />
                        <div className="md:col-span-2 mt-2 border-t border-slate-200 pt-4"><h3 className="font-extrabold text-slate-900">Headquarters Address</h3><p className="text-xs text-slate-500">Leave blank for Global access.</p></div>
                        <div className="md:col-span-2"><SelectField label="Country" value={organizationForm.headquartersAddress.country} onChange={event => setOrganizationForm({ ...organizationForm, headquartersAddress: { ...organizationForm.headquartersAddress, country: event.target.value, countryCode: (COUNTRY_CODES as any)[event.target.value]?.iso2 || '', administrativeArea: '' } })}><option value="">Select country</option>{COUNTRIES.map(country => <option key={country} value={country}>{country}</option>)}</SelectField></div>
                        <Field label="Address Line 1" value={organizationForm.headquartersAddress.addressLine1} onChange={event => setOrganizationForm({ ...organizationForm, headquartersAddress: { ...organizationForm.headquartersAddress, addressLine1: event.target.value } })} />
                        <Field label="Address Line 2" value={organizationForm.headquartersAddress.addressLine2} onChange={event => setOrganizationForm({ ...organizationForm, headquartersAddress: { ...organizationForm.headquartersAddress, addressLine2: event.target.value } })} />
                        <Field label="City / Locality" value={organizationForm.headquartersAddress.city} onChange={event => setOrganizationForm({ ...organizationForm, headquartersAddress: { ...organizationForm.headquartersAddress, city: event.target.value } })} />
                        <AddressSubdivisionField country={organizationForm.headquartersAddress.country} value={organizationForm.headquartersAddress.administrativeArea} onChange={value => setOrganizationForm({ ...organizationForm, headquartersAddress: { ...organizationForm.headquartersAddress, administrativeArea: value } })} className={subdivisionInputClass} />
                        <Field label="Postal Code" value={organizationForm.headquartersAddress.postalCode} onChange={event => setOrganizationForm({ ...organizationForm, headquartersAddress: { ...organizationForm.headquartersAddress, postalCode: event.target.value } })} />
                        <div className="md:col-span-2"><button disabled={saving} className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-50">Create Organization</button></div>
                    </form>
                </Modal>
            )}
        </div>
    );
};

export default PlatformOrganizationManagement;
